'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, ChainId, MarketId } from '@morpho-org/blue-sdk';
import { toBigInt } from '@morpho-org/dapp-core';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { ShieldCheckGradient } from '@repo/ui/icons';
import { memo, type ReactNode, useMemo } from 'react';
import { useAutoDeleverage } from '../hooks/useAutoDeleverage';
import { useIsAutoDeleverageEnabled } from '../hooks/useIsAutoDeleverageEnabled';
interface ProtectedLltvProps {
  lltv: BigIntish | null | undefined;
  marketId: MarketId;
  chainId: ChainId;
  skeleton?: ReactNode;
  tooltip?: string;
}
const ProtectedLltv = t0 => {
  const $ = _c(17);
  const {
    lltv,
    marketId,
    chainId,
    skeleton,
    tooltip
  } = t0;
  const isAutoDeleverageEnabled = useIsAutoDeleverageEnabled(chainId);
  const {
    status: autoDeleverageStatus,
    preLltv
  } = useAutoDeleverage(marketId, chainId);
  let t1;
  if ($[0] !== lltv) {
    t1 = formatters.percent.unit("%").trailingZero(false).digits(2).of(toBigInt(lltv), 18);
    $[0] = lltv;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedLltv = t1;
  let t2;
  if ($[2] !== preLltv) {
    t2 = formatters.percent.unit("%").trailingZero(false).digits(2).of(preLltv, 18);
    $[2] = preLltv;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formattedPreLltv = t2;
  if (!isAutoDeleverageEnabled) {
    return formattedLltv;
  }
  if (autoDeleverageStatus === "loading") {
    let t3;
    if ($[4] !== skeleton) {
      t3 = skeleton || <Skeleton className="h-md w-[70px] rounded-[20px]" />;
      $[4] = skeleton;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (autoDeleverageStatus === "authorized") {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TooltipTrigger asChild={true}><Icon icon={<ShieldCheckGradient />} /></TooltipTrigger>;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== tooltip) {
      t4 = !!tooltip && <TooltipContent>{tooltip}</TooltipContent>;
      $[7] = tooltip;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t4) {
      t5 = <Tooltip>{t3}{t4}</Tooltip>;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        lineHeight: "normal"
      };
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== formattedPreLltv) {
      t7 = <span style={t6}>{formattedPreLltv}</span>;
      $[12] = formattedPreLltv;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== t5 || $[15] !== t7) {
      t8 = <div className="flex items-center gap-[2px]">{t5}{t7}</div>;
      $[14] = t5;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  return formattedLltv;
};
export default memo(ProtectedLltv);